import {Emitter} from './emitter';
import {ConnectionQuality, ParticipantEvent, TrackEvent, type VideoResolution} from './enums';
import {SCREEN_MAX_PIXELS} from './moq';
import type {
	AudioCaptureOptions,
	DataPublishOptions,
	ScreenShareCaptureOptions,
	TrackPublishOptions,
	VideoCaptureOptions,
} from './options';
import {LocalTrackPublication, type RemoteTrackPublication, type TrackPublication} from './publication';
import {captureConstraints, LocalAudioTrack, type LocalTrack, LocalVideoTrack, Track} from './track';

export interface ParticipantPermission {
	canPublish: boolean;
	canSubscribe: boolean;
	canPublishData: boolean;
	hidden: boolean;
}

export abstract class Participant extends Emitter<string> {
	sid: string;
	identity: string;
	name?: string;
	metadata?: string;
	attributes: Readonly<Record<string, string>> = {};
	permissions?: ParticipantPermission;
	joinedAt?: Date;
	lastSpokeAt?: Date;
	audioLevel = 0;
	isSpeaking = false;
	connectionQuality: ConnectionQuality = ConnectionQuality.Unknown;
	trackPublications = new Map<string, TrackPublication>();
	audioTrackPublications = new Map<string, TrackPublication>();
	videoTrackPublications = new Map<string, TrackPublication>();

	protected constructor(sid: string, identity: string, name?: string) {
		super();
		this.sid = sid;
		this.identity = identity;
		this.name = name;
		this.joinedAt = new Date();
	}

	abstract get isLocal(): boolean;

	getTrackPublication(source: Track.Source): TrackPublication | undefined {
		for (const pub of this.trackPublications.values()) if (pub.source === source) return pub;
		return undefined;
	}

	getTrackPublications(): Array<TrackPublication> {
		return [...this.trackPublications.values()];
	}

	getTrackPublicationByName(name: string): TrackPublication | undefined {
		for (const pub of this.trackPublications.values()) if (pub.trackName === name) return pub;
		return undefined;
	}

	#enabled(source: Track.Source): boolean {
		const pub = this.getTrackPublication(source);
		return !!pub && !pub.isMuted;
	}

	get isCameraEnabled(): boolean {
		return this.#enabled(Track.Source.Camera);
	}

	get isMicrophoneEnabled(): boolean {
		return this.#enabled(Track.Source.Microphone);
	}

	get isScreenShareEnabled(): boolean {
		return !!this.getTrackPublication(Track.Source.ScreenShare);
	}

	get isEncrypted(): boolean {
		return false;
	}

	/** @internal */
	addPublication(pub: TrackPublication): void {
		this.trackPublications.set(pub.trackSid, pub);
		(pub.kind === Track.Kind.Audio ? this.audioTrackPublications : this.videoTrackPublications).set(pub.trackSid, pub);
	}

	/** @internal */
	removePublication(sid: string): TrackPublication | undefined {
		const pub = this.trackPublications.get(sid);
		this.trackPublications.delete(sid);
		this.audioTrackPublications.delete(sid);
		this.videoTrackPublications.delete(sid);
		return pub;
	}

	/** @internal */
	setSpeaking(speaking: boolean, level: number): void {
		this.audioLevel = level;
		if (speaking) this.lastSpokeAt = new Date();
		if (this.isSpeaking === speaking) return;
		this.isSpeaking = speaking;
		this.emit(ParticipantEvent.IsSpeakingChanged, speaking);
	}

	/** @internal */
	setConnectionQuality(quality: ConnectionQuality): void {
		if (this.connectionQuality === quality) return;
		this.connectionQuality = quality;
		this.emit(ParticipantEvent.ConnectionQualityChanged, quality);
	}
}

/** Implemented by the MoQ transport for the local participant. */
export interface LocalPublisher {
	publish(pub: LocalTrackPublication): void;
	unpublish(pub: LocalTrackPublication): void;
	publishData(data: Uint8Array, options: DataPublishOptions): Promise<void>;
}

export class LocalParticipant extends Participant {
	/** @internal set by Room while connected */
	publisher?: LocalPublisher;
	/** @internal set by Room: emits the matching room event */
	roomEmit?: (event: string, ...args: Array<unknown>) => void;
	audioCaptureDefaults: AudioCaptureOptions = {};
	videoCaptureDefaults: VideoCaptureOptions = {};
	publishDefaults: TrackPublishOptions = {};
	override trackPublications = new Map<string, LocalTrackPublication>();
	override audioTrackPublications = new Map<string, LocalTrackPublication>();
	override videoTrackPublications = new Map<string, LocalTrackPublication>();

	constructor(sid: string, identity: string, name?: string) {
		super(sid, identity, name);
		this.permissions = {canPublish: true, canSubscribe: true, canPublishData: true, hidden: false};
	}

	get isLocal(): true {
		return true;
	}

	override getTrackPublication(source: Track.Source): LocalTrackPublication | undefined {
		return super.getTrackPublication(source) as LocalTrackPublication | undefined;
	}

	override getTrackPublications(): Array<LocalTrackPublication> {
		return super.getTrackPublications() as Array<LocalTrackPublication>;
	}

	async publishTrack(
		track: LocalTrack | MediaStreamTrack,
		options?: TrackPublishOptions,
	): Promise<LocalTrackPublication> {
		let local: LocalTrack;
		if (track instanceof MediaStreamTrack) {
			local =
				track.kind === 'audio'
					? new LocalAudioTrack(track, undefined, true)
					: new LocalVideoTrack(track, undefined, true);
		} else {
			local = track;
		}
		const merged = {...this.publishDefaults, ...options};
		if (merged.source) local.source = merged.source;
		for (const existing of this.trackPublications.values()) {
			if (existing.track === local) return existing;
		}
		const pub = new LocalTrackPublication(local, merged);
		this.addPublication(pub);
		const forward = (event: string) => () => {
			this.emit(event === TrackEvent.Muted ? ParticipantEvent.TrackMuted : ParticipantEvent.TrackUnmuted, pub);
			this.roomEmit?.(event === TrackEvent.Muted ? 'trackMuted' : 'trackUnmuted', pub, this);
		};
		pub.on(TrackEvent.Muted, forward(TrackEvent.Muted));
		pub.on(TrackEvent.Unmuted, forward(TrackEvent.Unmuted));
		this.publisher?.publish(pub);
		this.emit(ParticipantEvent.LocalTrackPublished, pub);
		this.roomEmit?.('localTrackPublished', pub, this);
		return pub;
	}

	/** LiveKit's createTracks: capture without publishing, merged with the capture defaults. */
	async createTracks(options: {
		audio?: boolean | AudioCaptureOptions;
		video?: boolean | VideoCaptureOptions;
	}): Promise<Array<LocalTrack>> {
		const tracks: Array<LocalTrack> = [];
		if (options.audio) {
			const audio = await createLocalAudioTrack({
				...this.audioCaptureDefaults,
				...(options.audio === true ? {} : options.audio),
			});
			audio.source = Track.Source.Microphone;
			tracks.push(audio);
		}
		if (options.video) {
			try {
				const video = await createLocalVideoTrack({
					...this.videoCaptureDefaults,
					...(options.video === true ? {} : options.video),
				});
				video.source = Track.Source.Camera;
				tracks.push(video);
			} catch (error) {
				for (const track of tracks) track.stop();
				throw error;
			}
		}
		return tracks;
	}

	async unpublishTrack(
		track: LocalTrack | MediaStreamTrack,
		stopOnUnpublish = true,
	): Promise<LocalTrackPublication | undefined> {
		const pub = [...this.trackPublications.values()].find(
			(p) => p.track === track || p.track?.mediaStreamTrack === track,
		);
		if (!pub) return undefined;
		this.publisher?.unpublish(pub);
		this.removePublication(pub.trackSid);
		const local = pub.track;
		if (stopOnUnpublish) local?.stop();
		local?.bindSink(undefined);
		this.emit(ParticipantEvent.LocalTrackUnpublished, pub);
		this.roomEmit?.('localTrackUnpublished', pub, this);
		return pub;
	}

	async unpublishTracks(tracks: Array<LocalTrack | MediaStreamTrack>): Promise<Array<LocalTrackPublication>> {
		const out: Array<LocalTrackPublication> = [];
		for (const t of tracks) {
			const pub = await this.unpublishTrack(t);
			if (pub) out.push(pub);
		}
		return out;
	}

	async setMicrophoneEnabled(
		enabled: boolean,
		options?: AudioCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<LocalTrackPublication | undefined> {
		const existing = this.getTrackPublication(Track.Source.Microphone);
		if (enabled) {
			if (existing?.track) {
				await existing.unmute();
				return existing;
			}
			const merged = {...this.audioCaptureDefaults, ...options};
			const stream = await navigator.mediaDevices.getUserMedia({audio: captureConstraints(merged)});
			const track = new LocalAudioTrack(stream.getAudioTracks()[0], captureConstraints(merged), false);
			if (merged.processor) await track.setProcessor(merged.processor);
			return this.publishTrack(track, {...publishOptions, source: Track.Source.Microphone});
		}
		if (existing) await existing.mute();
		return existing;
	}

	async setCameraEnabled(
		enabled: boolean,
		options?: VideoCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<LocalTrackPublication | undefined> {
		const existing = this.getTrackPublication(Track.Source.Camera);
		if (enabled) {
			if (existing?.track) {
				await existing.unmute();
				return existing;
			}
			const merged = {...this.videoCaptureDefaults, ...options};
			const track = await createLocalVideoTrack(merged);
			return this.publishTrack(track, {...publishOptions, source: Track.Source.Camera});
		}
		if (existing?.track) await this.unpublishTrack(existing.track);
		return existing;
	}

	async setScreenShareEnabled(
		enabled: boolean,
		options?: ScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
		audioPublishOptions?: TrackPublishOptions,
	): Promise<LocalTrackPublication | undefined> {
		const existing = this.getTrackPublication(Track.Source.ScreenShare);
		if (!enabled) {
			const audio = this.getTrackPublication(Track.Source.ScreenShareAudio);
			if (audio?.track) await this.unpublishTrack(audio.track);
			if (existing?.track) await this.unpublishTrack(existing.track);
			return existing;
		}
		if (existing) return existing;
		const stream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions(options));
		const [video] = stream.getVideoTracks();
		const [audio] = stream.getAudioTracks();
		if (options?.contentHint) video.contentHint = options.contentHint;
		const pub = await this.publishTrack(new LocalVideoTrack(video, undefined, false), {
			...publishOptions,
			source: Track.Source.ScreenShare,
		});
		if (audio)
			await this.publishTrack(new LocalAudioTrack(audio, undefined, false), {
				...audioPublishOptions,
				source: Track.Source.ScreenShareAudio,
			});
		return pub;
	}

	async publishData(data: Uint8Array, options: DataPublishOptions = {}): Promise<void> {
		await this.publisher?.publishData(data, options);
	}
}

export class RemoteParticipant extends Participant {
	override trackPublications = new Map<string, RemoteTrackPublication>();
	override audioTrackPublications = new Map<string, RemoteTrackPublication>();
	override videoTrackPublications = new Map<string, RemoteTrackPublication>();
	#volumes = new Map<Track.Source, number>();

	constructor(sid: string, identity: string, name?: string) {
		super(sid, identity, name);
	}

	get isLocal(): false {
		return false;
	}

	override getTrackPublication(source: Track.Source): RemoteTrackPublication | undefined {
		return super.getTrackPublication(source) as RemoteTrackPublication | undefined;
	}

	override getTrackPublications(): Array<RemoteTrackPublication> {
		return super.getTrackPublications() as Array<RemoteTrackPublication>;
	}

	setVolume(
		volume: number,
		source: Track.Source.Microphone | Track.Source.ScreenShareAudio = Track.Source.Microphone,
	): void {
		this.#volumes.set(source, volume);
		const track = this.getTrackPublication(source)?.audioTrack;
		track?.setVolume(volume);
	}

	getVolume(
		source: Track.Source.Microphone | Track.Source.ScreenShareAudio = Track.Source.Microphone,
	): number | undefined {
		return this.#volumes.get(source);
	}
}

/**
 * Ask for at most as many pixels as we can send (SCREEN_MAX_PIXELS in moq.ts).
 * Capturing a 1440p or 4K surface and then encoding it at 1080p costs the
 * sharer a downscale per frame and buys nobody anything: measured, a 4K
 * capture alone dropped the share to two pictures a second. The aspect ratio
 * is kept, so the "ultra"/"source" rungs still mean "as much as we can send".
 */
function capturePixels(resolution: VideoResolution): VideoResolution {
	const pixels = resolution.width * resolution.height;
	if (!pixels || pixels <= SCREEN_MAX_PIXELS) return resolution;
	const ratio = Math.sqrt(SCREEN_MAX_PIXELS / pixels);
	return {
		...resolution,
		width: Math.round(resolution.width * ratio),
		height: Math.round(resolution.height * ratio),
	};
}

/**
 * The capture options a screen share asks the browser for, LiveKit's mapping.
 *
 * `resolution` carries the user's choice from the stream settings (1280×720,
 * 1920×1080, … at 15/30/60 fps). Dropping it, as this did before, let the
 * browser hand out whatever it felt like — usually the whole desktop at its
 * native size, which then had to be squeezed into the encoder's budget. The
 * sizes stay `ideal` (a window is as large as it is), the frame rate is a hard
 * `max` so a 15 fps share does not cost 60 fps worth of encoding.
 */
function displayMediaOptions(options?: ScreenShareCaptureOptions): DisplayMediaStreamOptions {
	const resolution = options?.resolution && capturePixels(options.resolution);
	const video = options?.video === true ? {} : (options?.video ?? {});
	const constraints: MediaTrackConstraints = {
		...video,
		...(resolution
			? {
					width: {ideal: resolution.width},
					height: {ideal: resolution.height},
					...(resolution.frameRate ? {frameRate: {ideal: resolution.frameRate, max: resolution.frameRate}} : {}),
				}
			: {}),
	};
	return {
		video: Object.keys(constraints).length ? constraints : true,
		audio: options?.audio ? (options.audio === true ? true : captureConstraints(options.audio)) : false,
		// Passed through for the browser's picker; unknown keys are ignored.
		...(options?.selfBrowserSurface ? {selfBrowserSurface: options.selfBrowserSurface} : {}),
		...(options?.surfaceSwitching ? {surfaceSwitching: options.surfaceSwitching} : {}),
		...(options?.systemAudio ? {systemAudio: options.systemAudio} : {}),
		...(options?.monitorTypeSurfaces ? {monitorTypeSurfaces: options.monitorTypeSurfaces} : {}),
		...(options?.preferCurrentTab ? {preferCurrentTab: options.preferCurrentTab} : {}),
	} as DisplayMediaStreamOptions;
}

export async function createLocalVideoTrack(options: VideoCaptureOptions = {}): Promise<LocalVideoTrack> {
	const constraints = captureConstraints(options);
	const stream = await navigator.mediaDevices.getUserMedia({
		video: Object.keys(constraints).length ? constraints : true,
	});
	const track = new LocalVideoTrack(stream.getVideoTracks()[0], constraints, false);
	if (options.processor) await track.setProcessor(options.processor);
	return track;
}

export async function createLocalAudioTrack(options: AudioCaptureOptions = {}): Promise<LocalAudioTrack> {
	const constraints = captureConstraints(options);
	const stream = await navigator.mediaDevices.getUserMedia({
		audio: Object.keys(constraints).length ? constraints : true,
	});
	const track = new LocalAudioTrack(stream.getAudioTracks()[0], constraints, false);
	if (options.processor) await track.setProcessor(options.processor);
	return track;
}
